import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Command, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator
} from './ui/command';
import { Dialog, DialogContent } from './ui/dialog';
import { getVisibleModules } from '../config/modules';
import {
  Search, LayoutDashboard, Monitor, ChefHat, Plus,
  UtensilsCrossed, FileText, Calculator,  Bell,
  ShoppingCart, Receipt, Coins, ArrowRight
} from 'lucide-react';

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeBranch, authHeaders, API, businessType, hasPermission } = useAuth();

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Reset on close
  useEffect(() => {
    if (!open) { setQuery(''); setSearchResults([]); }
  }, [open]);

  // Search API debounce
  useEffect(() => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = activeBranch ? `&branch_id=${activeBranch.id}` : '';
        const { data } = await axios.get(`${API}/search?q=${encodeURIComponent(query)}${params}`, authHeaders());
        setSearchResults(data.results || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, activeBranch, API, authHeaders]);

  const go = useCallback((path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  }, [navigate]);

  const visibleModules = getVisibleModules(businessType, hasPermission);

  // Static pages
  const pages = [
    { label: t('modules.commandCenter'), path: '/dashboard', icon: LayoutDashboard },
    { label: t('modules.pos'), path: '/dashboard/pos', icon: Monitor },
    { label: t('modules.preparation'), path: '/dashboard/kitchen', icon: ChefHat },
  ];
  visibleModules.forEach(mod => {
    if (mod.items) {
      mod.items.forEach(item => {
        pages.push({ label: t(item.labelKey), path: item.path, icon: item.icon, group: t(mod.labelKey) });
      });
    }
  });

  // Quick actions
  const actions = [
    { label: t('cmdPalette.addMenuItem'), path: '/dashboard/operations/menu', icon: Plus },
    { label: t('cmdPalette.addExpense'), path: '/dashboard/finance/accounting', icon: Coins },
    { label: t('cmdPalette.openPOS'), path: '/dashboard/pos', icon: Monitor },
    { label: t('cmdPalette.viewAlerts'), path: '/dashboard/finance/accounting', icon: Bell },
  ];

  const typeIcons = { menu_item: UtensilsCrossed, order: ShoppingCart, invoice: FileText, expense: Receipt };
  const typeLabels = { menu_item: t('cmdPalette.menuItems'), order: t('cmdPalette.orders'), invoice: t('cmdPalette.invoices'), expense: t('cmdPalette.expenses') };

  // Group search results by type
  const groupedResults = {};
  searchResults.forEach(r => {
    if (!groupedResults[r.type]) groupedResults[r.type] = [];
    groupedResults[r.type].push(r);
  });

  const hasApiResults = searchResults.length > 0;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="command-palette-trigger"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/50 bg-muted/40 hover:bg-muted/80 text-muted-foreground text-xs transition-all cursor-pointer">
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:block">{t('cmdPalette.searchPlaceholder')}</span>
        <kbd className="hidden md:inline-flex h-5 items-center gap-0.5 rounded border border-border/60 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[520px] gap-0" data-testid="command-palette-dialog"
          aria-describedby={undefined}>
          <div className="sr-only" role="heading" aria-level="2">{t('cmdPalette.searchPlaceholder')}</div>
          <Command shouldFilter={!hasApiResults && query.length < 2}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
            <CommandInput
              placeholder={t('cmdPalette.searchPlaceholder')}
              value={query}
              onValueChange={setQuery}
              data-testid="command-palette-input"
            />
            <CommandList className="max-h-[380px]">
              {query.length >= 2 && !searching && searchResults.length === 0 && (
                <CommandEmpty>{t('cmdPalette.noResults')}</CommandEmpty>
              )}
              {searching && <div className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</div>}

              {/* API search results */}
              {Object.entries(groupedResults).map(([type, items]) => {
                const TypeIcon = typeIcons[type] || FileText;
                return (
                  <CommandGroup key={type} heading={typeLabels[type] || type}>
                    {items.map((item) => (
                      <CommandItem key={`${type}-${item.id}`} value={`${item.title} ${item.subtitle}`}
                        onSelect={() => go(item.path)}
                        data-testid={`search-result-${type}`}
                        className="flex items-center gap-3 py-2.5 cursor-pointer">
                        <div className="w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
                          <TypeIcon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          {item.subtitle && <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">{item.meta}</span>
                        <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}

              {/* Quick Actions - show when no API results */}
              {!hasApiResults && !searching && (
                <CommandGroup heading={t('cmdPalette.quickActions')}>
                  {actions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <CommandItem key={action.label} value={action.label}
                        onSelect={() => go(action.path)}
                        className="flex items-center gap-3 py-2 cursor-pointer">
                        <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                          <Icon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-sm">{action.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Pages - show when no API results */}
              {!hasApiResults && !searching && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading={t('cmdPalette.pages')}>
                    {pages.map((page) => {
                      const Icon = page.icon;
                      return (
                        <CommandItem key={page.path} value={page.label}
                          onSelect={() => go(page.path)}
                          data-testid={`page-${page.path.split('/').pop()}`}
                          className="flex items-center gap-3 py-2 cursor-pointer">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{page.label}</span>
                          {page.group && <span className="text-[10px] text-muted-foreground/60 ms-auto">{page.group}</span>}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>

            {/* Footer */}
            <div className="border-t border-border/50 px-3 py-2 flex items-center gap-4 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="px-1 rounded bg-muted text-[10px]">↑↓</kbd> {t('cmdPalette.navigate')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 rounded bg-muted text-[10px]">↵</kbd> {t('cmdPalette.select')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 rounded bg-muted text-[10px]">Esc</kbd> {t('cmdPalette.close')}
              </span>
            </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
